import React from 'react';
import { FEATURE_KEYS } from '../featureConstants';
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  MedicineBoxOutlined,
  AppstoreOutlined,
  InboxOutlined,
  TagsOutlined,
  TeamOutlined,
  CreditCardOutlined,
  DollarOutlined,
  SettingOutlined,
  BranchesOutlined,
  AuditOutlined,
  MobileOutlined,
  ToolOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  CoffeeOutlined,
  OrderedListOutlined,
  BookOutlined,
  ClockCircleOutlined,
  TableOutlined,
  ContainerOutlined,
  CarOutlined,
  DropboxOutlined,
  UserSwitchOutlined,
  FileProtectOutlined,
  ExperimentOutlined,
  ShopOutlined,
  HistoryOutlined,
  WalletOutlined,
  DatabaseOutlined,
  SwapOutlined,
  SolutionOutlined,
  MedicineBoxFilled,
  FileAddOutlined,
  ReconciliationOutlined,
  FundOutlined,
  GoldOutlined,
  SnippetsOutlined,
  TruckOutlined,
  GlobalOutlined,
  UserOutlined,
  FileTextOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  HeartOutlined,
  EnvironmentOutlined,
  ThunderboltOutlined,
  PictureOutlined,
  WarningOutlined,
  SkinOutlined,
  ScissorOutlined,
  LockOutlined,
  CalendarOutlined,
  CrownOutlined,
  DeleteOutlined,
  StarOutlined,
} from '@ant-design/icons';
import urls from '../../routes/urls';

export interface MenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  permissions?: string[];
  featureKey?: number;
}

// ============================================================================
// OVERVIEW SECTION - Common to all business types
// ============================================================================
export const getOverviewMenuItems = (
  translate: (key: string) => string, 
  businessType?: string,
  hasPermission?: (key: number) => boolean
): MenuItem[] => {
  let itemKey: string = 'dashboard';
  let dashboardPath: string = urls.dashboard;
  let featureKey: number = FEATURE_KEYS.DASHBOARD;

  const type = businessType?.toLowerCase() || '';
  if (type === 'restaurant') {
    dashboardPath = urls.restaurantDashboard;
    featureKey = FEATURE_KEYS.RESTAURANT_DASHBOARD;
    itemKey = 'res_dashboard';

    if (hasPermission) {
      if (hasPermission(FEATURE_KEYS.RESTAURANT_DASHBOARD)) {
        dashboardPath = urls.restaurantDashboard;
        featureKey = FEATURE_KEYS.RESTAURANT_DASHBOARD;
      } else if (hasPermission(FEATURE_KEYS.RESTAURANT_TABLES)) {
        dashboardPath = urls.restaurantTables;
        featureKey = FEATURE_KEYS.RESTAURANT_TABLES;
      }
    }
  } else if (type === 'bar' || type === 'club') {
    dashboardPath = urls.barDashboard;
    featureKey = FEATURE_KEYS.BAR_DASHBOARD;
    itemKey = 'bar_dashboard_item';

    if (hasPermission) {
      if (hasPermission(FEATURE_KEYS.BAR_DASHBOARD)) {
        dashboardPath = urls.barDashboard;
        featureKey = FEATURE_KEYS.BAR_DASHBOARD;
      } else if (hasPermission(FEATURE_KEYS.BAR_POS)) {
        dashboardPath = urls.barPOS;
        featureKey = FEATURE_KEYS.BAR_POS;
      }
    }
  } else if (type === 'pharmacy') {
    dashboardPath = urls.pharmacyDashboard;
    featureKey = FEATURE_KEYS.PHARMACY_DASHBOARD;
    itemKey = 'pharmacy_dashboard';

    if (hasPermission) {
      if (hasPermission(FEATURE_KEYS.PHARMACY_DASHBOARD)) {
        dashboardPath = urls.pharmacyDashboard;
        featureKey = FEATURE_KEYS.PHARMACY_DASHBOARD;
      } else if (hasPermission(FEATURE_KEYS.PHARMACY_POS)) {
        dashboardPath = urls.pharmacyPOS;
        featureKey = FEATURE_KEYS.PHARMACY_POS;
      }
    }
  } else if (type === 'hospital') {
    dashboardPath = urls.hospitalDashboard;
    featureKey = FEATURE_KEYS.HOSPITAL_DASHBOARD;
    itemKey = 'hospital_dashboard';

    if (hasPermission) {
      if (hasPermission(FEATURE_KEYS.HOSPITAL_DASHBOARD)) {
        dashboardPath = urls.hospitalDashboard;
        featureKey = FEATURE_KEYS.HOSPITAL_DASHBOARD;
      } else if (hasPermission(FEATURE_KEYS.CLINICAL_QUEUE)) {
        dashboardPath = urls.hospitalQueue;
        featureKey = FEATURE_KEYS.CLINICAL_QUEUE;
      } else if (hasPermission(FEATURE_KEYS.HOSPITAL_HR)) {
        dashboardPath = urls.hospitalHR;
        featureKey = FEATURE_KEYS.HOSPITAL_HR;
      } else if (hasPermission(FEATURE_KEYS.HOSPITAL_PAYROLL)) {
        dashboardPath = urls.hospitalPayroll;
        featureKey = FEATURE_KEYS.HOSPITAL_PAYROLL;
      } else if (hasPermission(FEATURE_KEYS.HOSPITAL_WARDS)) {
        dashboardPath = urls.hospitalWards;
        featureKey = FEATURE_KEYS.HOSPITAL_WARDS;
      } else if (hasPermission(FEATURE_KEYS.HOSPITAL_LABS)) {
        dashboardPath = urls.hospitalLabs;
        featureKey = FEATURE_KEYS.HOSPITAL_LABS;
      } else if (hasPermission(FEATURE_KEYS.HOSPITAL_MANAGEMENT)) {
        dashboardPath = urls.hospitalFinance;
        featureKey = FEATURE_KEYS.HOSPITAL_MANAGEMENT;
      }
    }
  } else if (type === 'shoe' || type.includes('shoe') || type.includes('footwear')) {
    return [
      {
        key: 'shoe_dashboard',
        label: translate('Executive Dashboard'),
        icon: <DashboardOutlined />,
        path: urls.shoeDashboard,
        featureKey: FEATURE_KEYS.SHOE_DASHBOARD,
      },
      {
        key: 'shoe_catalog',
        label: translate('Footwear Catalog'),
        icon: <AppstoreOutlined />,
        path: urls.shoeCatalog,
        featureKey: FEATURE_KEYS.SHOE_CATALOG,
      },
    ];
  } else if (
    type === 'clothing' || 
    type.includes('clothing') || 
    type.includes('garment') || 
    type.includes('apparel') || 
    type.includes('vetement') || 
    type.includes('vêtement') || 
    type.includes('habille')
  ) {
    dashboardPath = urls.clothingDashboard;
    featureKey = FEATURE_KEYS.CLOTHING_DASHBOARD;
    itemKey = 'clothing_dashboard';
  } else if (type === 'hardware' || type.includes('hardware')) {
    dashboardPath = urls.hardwareDashboard;
    featureKey = FEATURE_KEYS.HARDWARE_DASHBOARD;
    itemKey = 'hardware_dashboard';
  } else if (type === 'grocery' || type.includes('grocery') || type.includes('supermarket')) {
    dashboardPath = urls.groceryDashboard;
    featureKey = FEATURE_KEYS.GROCERY_DASHBOARD;
    itemKey = 'grocery_dashboard';
  } else if (type === 'jewelry' || type.includes('jewelry')) {
    dashboardPath = urls.jewelryDashboard;
    featureKey = FEATURE_KEYS.JEWELRY_DASHBOARD;
    itemKey = 'jewelry_dashboard';
  } else if (type === 'coffee_shop' || type.includes('coffee')) {
    dashboardPath = urls.coffeeDashboard;
    featureKey = FEATURE_KEYS.COFFEE_SHOP_DASHBOARD;
    itemKey = 'coffee_dashboard';
  } else if (type === 'real_estate' || type.includes('real_estate') || type.includes('property')) {
    dashboardPath = urls.realEstateDashboard;
    featureKey = FEATURE_KEYS.REAL_ESTATE_DASHBOARD;
    itemKey = 'real_estate_dashboard';
  } else if (type === 'barbershop' || type.includes('barber')) {
    dashboardPath = urls.barbershopDashboard;
    featureKey = FEATURE_KEYS.BARBERSHOP_DASHBOARD;
    itemKey = 'barbershop_dashboard';
  } else if (type === 'spa' || type.includes('wellness')) {
    dashboardPath = urls.spaDashboard;
    featureKey = FEATURE_KEYS.SPA_DASHBOARD;
    itemKey = 'spa_dashboard';
  } else if (type === 'tailoring' || type.includes('tailor') || type.includes('couture')) {
    dashboardPath = urls.tailoringDashboard;
    featureKey = FEATURE_KEYS.TAILORING_DASHBOARD;
    itemKey = 'tailoring_dashboard';
  } else if (type === 'freelancer' || type.includes('professional') || type.includes('consult')) {
    dashboardPath = urls.professionalServicesDashboard;
    featureKey = FEATURE_KEYS.PROFESSIONAL_SERVICES_DASHBOARD;
    itemKey = 'ps_dashboard';
  } else if (type === 'salon' || type.includes('salon') || type.includes('beauty')) {
    dashboardPath = urls.salonDashboard;
    featureKey = FEATURE_KEYS.SALON_DASHBOARD;
    itemKey = 'salon_dashboard';
  } else if (type === 'car_rental' || type.includes('car_rental') || type.includes('fleet')) {
    dashboardPath = urls.carRentalDashboard;
    featureKey = FEATURE_KEYS.CAR_RENTAL_DASHBOARD;
    itemKey = 'car_rental_dashboard';
  } else if (
    type === 'hotel' ||
    type.includes('hotel') ||
    type.includes('hostel') ||
    type.includes('hospitality') ||
    type.includes('lodge') ||
    type.includes('resort') ||
    type.includes('motel') ||
    type.includes('guesthouse') ||
    type.includes('guest_house') ||
    type.includes('auberge')
  ) {
    return [
      {
        key: 'hotel_dashboard',
        label: translate('Hotel Overview & Analytics'),
        icon: <DashboardOutlined />,
        path: urls.hotelDashboard,
        featureKey: FEATURE_KEYS.HOTEL_DASHBOARD,
      },
      {
        key: 'hotel_rooms',
        label: translate('Room Rack & Live Matrix'),
        icon: <AppstoreOutlined />,
        path: urls.hotelRooms,
        featureKey: FEATURE_KEYS.HOTEL_ROOMS,
      },
      {
        key: 'hotel_reservations',
        label: translate('Front Desk & Bookings'),
        icon: <CalendarOutlined />,
        path: urls.hotelReservations,
        featureKey: FEATURE_KEYS.HOTEL_RESERVATIONS,
      },
      {
        key: 'hotel_housekeeping',
        label: translate('Housekeeping & Turnover'),
        icon: <AuditOutlined />,
        path: urls.hotelHousekeeping,
        featureKey: FEATURE_KEYS.HOTEL_HOUSEKEEPING,
      },
      {
        key: 'hotel_folios',
        label: translate('Guest Folios & Billing'),
        icon: <CreditCardOutlined />,
        path: urls.hotelFolios,
        featureKey: FEATURE_KEYS.HOTEL_FOLIOS,
      },
      {
        key: 'hotel_expenses',
        label: translate('Hotel Expenses'),
        icon: <DollarOutlined />,
        path: urls.expenses,
        featureKey: FEATURE_KEYS.EXPENSES,
      },
      {
        key: 'hotel_sales_reports',
        label: translate('Sales & Revenue Reports'),
        icon: <LineChartOutlined />,
        path: urls.reports,
        featureKey: FEATURE_KEYS.REPORTS,
      },
      {
        key: 'settings',
        label: translate('Settings'),
        icon: <SettingOutlined />,
        path: urls.settings,
        featureKey: FEATURE_KEYS.SETTINGS,
      },
    ];
  } else if (
    type === 'mobile_money' ||
    type.includes('mobile_money') ||
    type.includes('agency_banking') ||
    type.includes('airtime') ||
    type.includes('momo')
  ) {
    return [
      {
        key: 'momo_dashboard',
        label: translate('Executive Dashboard'),
        icon: <DashboardOutlined />,
        path: urls.mobileMoneyDashboard,
        featureKey: FEATURE_KEYS.MOBILE_MONEY_DASHBOARD,
      },
      {
        key: 'momo_pos',
        label: translate('Agent Terminal POS'),
        icon: <MobileOutlined />,
        path: urls.mobileMoneyPOS,
        featureKey: FEATURE_KEYS.MOBILE_MONEY_POS,
      },
      {
        key: 'momo_floats',
        label: translate('E-Floats & Cash Till'),
        icon: <WalletOutlined />,
        path: urls.mobileMoneyFloats,
        featureKey: FEATURE_KEYS.MOBILE_MONEY_FLOATS,
      },
      {
        key: 'momo_ledger',
        label: translate('Digital Ledger'),
        icon: <BookOutlined />,
        path: urls.mobileMoneyLedger,
        featureKey: FEATURE_KEYS.MOBILE_MONEY_LEDGER,
      },
      {
        key: 'momo_reconciliation',
        label: translate('Shift Audit & Close'),
        icon: <AuditOutlined />,
        path: urls.mobileMoneyReconciliation,
        featureKey: FEATURE_KEYS.MOBILE_MONEY_RECONCILIATION,
      },
      {
        key: 'momo_debts',
        label: translate('Dettes & Crédits Client'),
        icon: <WarningOutlined />,
        path: urls.mobileMoneyDebts,
      },
      {
        key: 'momo_customer_history',
        label: translate('Historique Clients'),
        icon: <HistoryOutlined />,
        path: urls.mobileMoneyCustomerHistory,
      },
    ];
  } else {
    if (hasPermission) {
      if (hasPermission(FEATURE_KEYS.DASHBOARD)) {
        dashboardPath = urls.dashboard;
        featureKey = FEATURE_KEYS.DASHBOARD;
      } else if (hasPermission(FEATURE_KEYS.POS_V2)) {
        dashboardPath = urls.pos;
        featureKey = FEATURE_KEYS.POS_V2;
      } else if (hasPermission(FEATURE_KEYS.SALES)) {
        dashboardPath = urls.sales;
        featureKey = FEATURE_KEYS.SALES;
      }
    }
  }

  const items: MenuItem[] = [
    {
      key: itemKey,
      label: translate('Dashboard'),
      icon: <DashboardOutlined />,
      path: dashboardPath,
      featureKey: featureKey,
    },
  ];

  // Include Owner Dashboard for general retail or explicitly permitted
  if (!['shoe', 'clothing', 'hardware', 'grocery', 'jewelry', 'coffee_shop', 'real_estate', 'barbershop', 'spa', 'tailoring', 'freelancer', 'salon', 'car_rental'].includes(type)) {
    items.push({
      key: 'Owner Dashboard',
      label: type === 'hospital' ? translate('Clinical Director Console') : translate('Owner Dashboard'),
      icon: <GlobalOutlined />,
      path: urls.ownerDashboard,
      featureKey: FEATURE_KEYS.OWNER_DASHBOARD,
    });
  }

  return items;
};

// ============================================================================
// SALES SECTION - For Shop, Store, Retail, Supermarket
// ============================================================================
export const getSalesMenuItems = (translate: (key: string) => string, businessType?: string): MenuItem[] => {
  const type = businessType?.toLowerCase() || '';
  const isRetail = ['retail', 'boutique', 'supermarket', 'store', 'shop'].includes(type);
  const isHospitality = ['restaurant', 'bar', 'club', 'lounge'].includes(type);
  
  return [
    {
      key: 'sales',
      label: isRetail ? translate('Sales') : translate('Orders'),
      icon: <ShoppingCartOutlined />,
      featureKey: FEATURE_KEYS.SALES_ANALYTICS,
      children: [
        {
          key: 'pos',
          label: translate('Point of Sale'),
          icon: <ShoppingCartOutlined />,
          path: isHospitality ? urls.barPOS : urls.pos,
          featureKey: FEATURE_KEYS.POS_V2, 
        },
        {
          key: 'wholesale',
          label: translate('Wholesale'),
          icon: <GoldOutlined />,
          path: urls.wholesale,
          featureKey: FEATURE_KEYS.WHOLESALE,
        },
        {
          key: 'sales_history',
          label: translate('Sales History'),
          icon: <HistoryOutlined />,
          path: urls.salesHistory,
          featureKey: FEATURE_KEYS.SALES_HISTORY,
        },
      ],
    },
  ];
};

export const getPharmacyMenuItems = (translate: (key: string) => string, businessType?: string): MenuItem[] => {
  const isHospital = businessType === 'hospital';
  return [
    // Quick Actions — top priority for all pharmacists
    {
      key: 'pharmacy_pos',
      label: isHospital ? translate('Hospital Pharmacy POS') : translate('Pharmacy POS'),
      icon: <ShoppingCartOutlined />,
      path: urls.pharmacyPOS,
      featureKey: FEATURE_KEYS.PHARMACY_POS,
    },
    {
      key: 'pharmacy_sales_history',
      label: translate('Sales History'),
      icon: <HistoryOutlined />,
      path: urls.pharmacySalesHistory,
      featureKey: FEATURE_KEYS.PHARMACY_SALES,
    },
    {
      key: 'pharmacy_dashboard',
      label: isHospital ? translate('Hospital Pharmacy Dashboard') : translate('Pharmacy Dashboard'),
      icon: <DashboardOutlined />,
      path: urls.pharmacyDashboard,
      featureKey: FEATURE_KEYS.PHARMACY_DASHBOARD,
    },
    // Dispensing & Clinical
    {
      key: 'pharmacy_dispensing_group',
      label: translate('Dispensing'),
      icon: <MedicineBoxFilled />,
      children: [
        {
          key: 'pharmacy_dispensing',
          label: translate('Dispense Medication'),
          icon: <MedicineBoxFilled />,
          path: urls.pharmacyDispensing,
          featureKey: FEATURE_KEYS.DISPENSING,
        },
        {
          key: 'prescriptions',
          label: translate('Prescriptions'),
          icon: <FileProtectOutlined />,
          path: urls.prescriptions,
          featureKey: FEATURE_KEYS.PRESCRIPTIONS,
        },
        {
          key: 'patients',
          label: translate('Patients'),
          icon: <SolutionOutlined />,
          path: urls.patients,
          featureKey: FEATURE_KEYS.PATIENTS,
        },
      ],
    },
    // Stock & Medications
    {
      key: 'pharmacy_stock_group',
      label: translate('Stock & Medications'),
      icon: <DatabaseOutlined />,
      children: [
        {
          key: 'medications',
          label: translate('Medications'),
          icon: <ExperimentOutlined />,
          path: urls.medications,
          featureKey: FEATURE_KEYS.MEDICATIONS,
        },
        {
          key: 'pharmacy_purchase_orders',
          label: translate('Purchase Orders'),
          icon: <FileAddOutlined />,
          path: urls.pharmacyPurchaseOrders,
          featureKey: FEATURE_KEYS.PURCHASE_ORDERS,
        },
        {
          key: 'pharmacy_batches',
          label: translate('Batches & Expiry'),
          icon: <DatabaseOutlined />,
          path: urls.pharmacyBatches,
          featureKey: FEATURE_KEYS.PHARMACY_BATCHES,
        },
        {
          key: 'products',
          label: translate('Non-Medication Products'),
          icon: <InboxOutlined />,
          path: urls.products,
          featureKey: FEATURE_KEYS.PRODUCTS,
        },
        {
          key: 'categories',
          label: translate('Product Categories'),
          icon: <TagsOutlined />,
          path: urls.productCategory,
          featureKey: FEATURE_KEYS.CATEGORIES,
        },
      ],
    },
    // Procurement
    {
      key: 'pharmacy_procurement',
      label: translate('Procurement'),
      icon: <TruckOutlined />,
      featureKey: FEATURE_KEYS.PHARMACY_PROCUREMENT,
      children: [
        {
          key: 'pharmacy_purchase_orders_nested',
          label: translate('Purchase Orders'),
          icon: <FileAddOutlined />,
          path: urls.pharmacyPurchaseOrders,
          featureKey: FEATURE_KEYS.PURCHASE_ORDERS,
        },
        {
          key: 'pharmacy_goods_receiving',
          label: translate('Goods Receiving'),
          icon: <DropboxOutlined />,
          path: urls.pharmacyGoodsReceiving,
          featureKey: FEATURE_KEYS.DELIVERY_NOTES,
        },
        {
          key: 'pharmacy_stock_transfers',
          label: translate('Stock Transfers'),
          icon: <SwapOutlined />,
          path: urls.pharmacyStockTransfers,
          featureKey: FEATURE_KEYS.STOCK_MOVEMENTS,
        },
        {
          key: 'suppliers',
          label: translate('Suppliers'),
          icon: <ShopOutlined />,
          path: urls.suppliers,
          featureKey: FEATURE_KEYS.SUPPLIERS,
        },
      ],
    },
    // Reports
    {
      key: 'pharmacy_reports',
      label: translate('Reports'),
      icon: <BarChartOutlined />,
      path: urls.pharmacyReports,
      featureKey: FEATURE_KEYS.PHARMACY_REPORTS,
    },
    // Team
    {
      key: 'pharmacists',
      label: isHospital ? translate('Clinical Staff') : translate('Pharmacists'),
      icon: <UserSwitchOutlined />,
      path: urls.pharmacists,
      featureKey: FEATURE_KEYS.PHARMACISTS,
    },
  ];
};

// ============================================================================
// HOSPITALITY — Flat two-level, role-optimized
// Bar & Restaurant share this structure, content varies by type.
// ============================================================================
export const getHospitalityMenuItems = (translate: (key: string) => string, businessType: string): MenuItem[] => {
  const isRestaurant = businessType === 'restaurant' || businessType === 'coffee_shop';
  const isBar = businessType === 'bar' || businessType === 'club';

  const items: MenuItem[] = [];

  // ── 1. Quick Actions (always first — core job actions for waiters/bartenders) ──
  const quickActions: MenuItem[] = [];

  if (isRestaurant) {
    quickActions.push({
      key: 'restaurant_tables',
      label: translate('Tables View'),
      icon: <TableOutlined />,
      path: urls.restaurantTables,
      featureKey: FEATURE_KEYS.RESTAURANT_TABLES,
    });
    quickActions.push({
      key: 'restaurant_reservations',
      label: translate('Reservations'),
      icon: <CalendarOutlined />,
      path: urls.restaurantReservations,
      featureKey: FEATURE_KEYS.RESTAURANT_RESERVATIONS,
    });
  }

  quickActions.push({
    key: 'bar_pos',
    label: isRestaurant ? translate('New Order / POS') : translate('Bar POS'),
    icon: <ThunderboltOutlined />,
    path: urls.barPOS,
    featureKey: FEATURE_KEYS.BAR_POS,
  });

  quickActions.push({
    key: 'bar_tabs',
    label: translate('Active Orders'),
    icon: <OrderedListOutlined />,
    path: urls.barTabs,
    featureKey: FEATURE_KEYS.BAR_TABS,
  });

  if (isBar || isRestaurant) {
    quickActions.push({
      key: 'bar_comptoirs',
      label: translate('Service Points'),
      icon: <EnvironmentOutlined />,
      path: urls.comptoirs,
      featureKey: FEATURE_KEYS.COMPTOIRS,
    });
  }

  items.push({
    key: 'hosp_quick_actions',
    label: isRestaurant ? translate('Floor & Orders') : translate('Bar & Orders'),
    icon: <ThunderboltOutlined />,
    children: quickActions,
  });

  // ── 2. Kitchen / Menus (for restaurant manager, kitchen staff) ──
  const kitchenItems: MenuItem[] = [];

  if (isRestaurant) {
    kitchenItems.push({
      key: 'restaurant_kitchen',
      label: translate('Kitchen Display'),
      icon: <ReconciliationOutlined />,
      path: urls.restaurantKitchen,
      featureKey: FEATURE_KEYS.RESTAURANT_KITCHEN,
    });
  }

  kitchenItems.push({
    key: 'bar_menus',
    label: translate('Menu Management'),
    icon: <BookOutlined />,
    path: urls.barMenus,
    featureKey: FEATURE_KEYS.BAR_MENUS,
  });

  kitchenItems.push({
    key: 'bar_recipes',
    label: isRestaurant ? translate('Recipes & Prep') : translate('Recipes'),
    icon: <ExperimentOutlined />,
    path: urls.barRecipes,
    featureKey: FEATURE_KEYS.BAR_RECIPES,
  });

  if (isRestaurant) {
    kitchenItems.push({
      key: 'restaurant_table_terms',
      label: translate('Table Terms'),
      icon: <SettingOutlined />,
      path: urls.restaurantTableTerms,
      featureKey: FEATURE_KEYS.RESTAURANT_TABLE_TERMS,
    });
  }

  items.push({
    key: 'hosp_kitchen_group',
    label: isRestaurant ? translate('Kitchen & Menus') : translate('Menus & Recipes'),
    icon: <BookOutlined />,
    children: kitchenItems,
  });

  // ── 3. Stock & Operations ──
  const stockItems: MenuItem[] = [
    {
      key: 'bar_stock',
      label: isRestaurant ? translate('Kitchen Stock') : translate('Stock Inventory'),
      icon: <ContainerOutlined />,
      path: urls.barStock,
      featureKey: FEATURE_KEYS.BAR_STOCK,
    },
    {
      key: 'bar_movements',
      label: translate('Stock Movements'),
      icon: <SwapOutlined />,
      path: urls.barMovements,
      featureKey: FEATURE_KEYS.BAR_MOVEMENTS,
    },
  ];

  stockItems.push({
    key: 'bar_shifts',
    label: translate('Staff Shifts'),
    icon: <ClockCircleOutlined />,
    path: urls.barShifts,
    featureKey: FEATURE_KEYS.BAR_SHIFTS,
  });

  items.push({
    key: 'hosp_stock_group',
    label: isRestaurant ? translate('Stock & Operations') : translate('Stock & Shifts'),
    icon: <ContainerOutlined />,
    children: stockItems,
  });

  if (isRestaurant) {
    items.push({
      key: 'hosp_analytics_group',
      label: translate('Reports & Analytics'),
      icon: <BarChartOutlined />,
      children: [
        {
          key: 'restaurant_reports',
          label: translate('Table Turn Reports'),
          icon: <HistoryOutlined />,
          path: urls.restaurantTableReports,
          featureKey: FEATURE_KEYS.RESTAURANT_REPORTS,
        },
      ],
    });
  }

  return items;
};

// ============================================================================
// RETAIL SECTION - Shop, Store, Boutique, Supermarket
// ============================================================================
export const getRetailMenuItems = (translate: (key: string) => string, businessType?: string): MenuItem[] => {
  return [
    // Quick Sale
    {
      key: 'pos',
      label: translate('Point of Sale'),
      icon: <ShoppingCartOutlined />,
      path: urls.pos,
      featureKey: FEATURE_KEYS.POS_V2,
    },
    {
      key: 'wholesale',
      label: translate('Wholesale'),
      icon: <GoldOutlined />,
      path: urls.wholesale,
      featureKey: FEATURE_KEYS.WHOLESALE,
    },
    {
      key: 'retail_inventory_group',
      label: translate('Products & Inventory'),
      icon: <AppstoreOutlined />,
      children: [
        {
          key: 'products',
          label: translate('Products'),
          icon: <InboxOutlined />,
          path: urls.products,
          featureKey: FEATURE_KEYS.PRODUCTS,
        },
        {
          key: 'categories',
          label: translate('Categories'),
          icon: <TagsOutlined />,
          path: urls.productCategory,
          featureKey: FEATURE_KEYS.CATEGORIES,
        },
        {
          key: 'stock_movements',
          label: translate('Stock Movements'),
          icon: <SwapOutlined />,
          path: urls.stockMovements,
          featureKey: FEATURE_KEYS.STOCK_MOVEMENTS,
        },
        {
          key: 'Retail Inventory',
          label: translate('Inventory Overview'),
          icon: <BarChartOutlined />,
          path: urls.retailInventory,
          featureKey: FEATURE_KEYS.RETAIL_INVENTORY,
        },
        {
          key: 'store_fulfillment',
          label: translate('Store Fulfillment'),
          icon: <TruckOutlined />,
          path: urls.storeFulfillment,
          featureKey: FEATURE_KEYS.STORE_FULFILLMENT,
        },
      ],
    },
    // Procurement
    {
      key: 'retail_procurement_group',
      label: translate('Procurement'),
      icon: <TruckOutlined />,
      children: [
        {
          key: 'purchase_orders',
          label: translate('Purchase Orders'),
          icon: <FileAddOutlined />,
          path: urls.purchaseOrders,
          featureKey: FEATURE_KEYS.PURCHASE_ORDERS,
        },
        {
          key: 'delivery_notes',
          label: translate('Delivery Notes'),
          icon: <SnippetsOutlined />,
          path: urls.deliveryNotes,
          featureKey: FEATURE_KEYS.DELIVERY_NOTES,
        },
        {
          key: 'suppliers',
          label: translate('Suppliers'),
          icon: <ShopOutlined />,
          path: urls.suppliers,
          featureKey: FEATURE_KEYS.SUPPLIERS,
        },
      ],
    },
    // Reports
    {
      key: 'retail_reports_group',
      label: translate('Reports'),
      icon: <PieChartOutlined />,
      children: [
        {
          key: 'Retail Reports',
          label: translate('Sales Reports'),
          icon: <PieChartOutlined />,
          path: urls.retailReports,
          featureKey: FEATURE_KEYS.RETAIL_REPORTS,
        },
      ],
    },
  ];
};

// ============================================================================
// INVENTORY SECTION - Common inventory management (non-retail/bar)
// ============================================================================
export const getInventoryMenuItems = (translate: (key: string) => string, businessType?: string): MenuItem[] => {
  const isRestaurant = businessType === 'restaurant' || businessType === 'coffee_shop';
  const isBar = ['bar', 'club', 'lounge'].includes(businessType || '');
  
  const productsLabel = isRestaurant ? translate('Ingredients & Items') : 
                        isBar ? translate('Stock Items') : 
                        translate('Products');

  return [
    {
      key: 'inventory',
      label: translate('Inventory'),
      icon: <AppstoreOutlined />,
      featureKey: FEATURE_KEYS.INVENTORY,
      children: [
        {
          key: 'products',
          label: productsLabel,
          icon: <InboxOutlined />,
          path: urls.products,
          featureKey: FEATURE_KEYS.PRODUCTS,
        },
        {
          key: 'categories',
          label: translate('Categories'),
          icon: <TagsOutlined />,
          path: urls.productCategory,
          featureKey: FEATURE_KEYS.CATEGORIES,
        },
        {
          key: 'stock_movements',
          label: translate('Stock Movements'),
          icon: <SwapOutlined />,
          path: urls.stockMovements,
          featureKey: FEATURE_KEYS.STOCK_MOVEMENTS,
        },
      ],
    },
  ];
};

// ============================================================================
// PROCUREMENT SECTION - Purchase orders and delivery (non-retail)
// ============================================================================
export const getProcurementMenuItems = (translate: (key: string) => string, businessType?: string): MenuItem[] => [
  {
    key: 'procurement',
    label: translate('Procurement'),
    icon: <TruckOutlined />,
    featureKey: FEATURE_KEYS.PROCUREMENT,
    children: [
      {
        key: 'purchase_orders',
        label: translate('Purchase Orders'),
        icon: <FileAddOutlined />,
        path: urls.purchaseOrders,
        featureKey: FEATURE_KEYS.PURCHASE_ORDERS,
      },
      {
        key: 'delivery_notes',
        label: translate('Delivery Notes'),
        icon: <SnippetsOutlined />,
        path: urls.deliveryNotes,
        featureKey: FEATURE_KEYS.DELIVERY_NOTES,
      },
      {
        key: 'suppliers',
        label: translate('Suppliers'),
        icon: <ShopOutlined />,
        path: urls.suppliers,
        featureKey: FEATURE_KEYS.SUPPLIERS,
      },
    ],
  },
];

// ============================================================================
// CUSTOMERS SECTION
// ============================================================================
export const getCustomerMenuItems = (translate: (key: string) => string, businessType?: string): MenuItem[] => {
  const isHospitality = ['restaurant', 'bar', 'club', 'lounge', 'hotel'].includes(businessType?.toLowerCase() || '');
  const isMedical = ['hospital', 'clinic', 'pharmacy'].includes(businessType?.toLowerCase() || '');

  const items: MenuItem[] = [];

  if (!isMedical) {
     items.push({
      key: 'customers',
      label: isHospitality ? translate('Guests') : translate('Customers'),
      icon: <TeamOutlined />,
      path: urls.customers,
      featureKey: isHospitality ? FEATURE_KEYS.RESTAURANT_RESERVATIONS : FEATURE_KEYS.CUSTOMERS,
    });
  }

  items.push({
      key: 'debts',
      label: translate('Debts'),
      icon: <CreditCardOutlined />,
      path: urls.debts,
      featureKey: FEATURE_KEYS.DEBTS,
  });

  return items;
};

// ============================================================================
// ANALYTICS SECTION
// ============================================================================
export const getAnalyticsMenuItems = (translate: (key: string) => string, businessType?: string): MenuItem[] => [
  {
    key: 'analytics',
    label: translate('Analytics'),
    icon: <BarChartOutlined />,
    children: [
      {
        key: 'sales_analytics',
        label: translate('Sales Analytics'),
        icon: <LineChartOutlined />,
        path: urls.salesAnalytics,
        featureKey: FEATURE_KEYS.SALES_ANALYTICS,
      },
      {
        key: 'financial_analytics',
        label: translate('Financial Analytics'),
        icon: <FundOutlined />,
        path: urls.financialAnalytics,
        featureKey: FEATURE_KEYS.FINANCIAL_ANALYTICS,
      },
      ...(['restaurant', 'bar', 'club', 'lounge', 'snack_bar'].includes(businessType || '') ? [
        {
          key: 'bar_analytics_global',
          label: translate('Bar & Restaurant Reports'),
          icon: <BarChartOutlined />,
          path: urls.barAnalytics,
          featureKey: FEATURE_KEYS.BAR_ANALYTICS,
        }
      ] : []),
    ],
  },
];

// ============================================================================
// FINANCE & ADMIN SECTION
// ============================================================================
export const getFinanceAdminMenuItems = (translate: (key: string) => string, businessType?: string): MenuItem[] => {
  const items: MenuItem[] = [];

  // Include Finance submenu only for business types that do not have Funds & Expenses in their dedicated operations block
  if (
    businessType !== 'hardware' &&
    businessType !== 'shoe' &&
    businessType !== 'mobile_money' &&
    businessType !== 'momo' &&
    businessType !== 'agent'
  ) {
    items.push({
      key: 'finance',
      label: translate('Finance'),
      icon: <DollarOutlined />,
      children: [
        {
          key: 'funds',
          label: translate('Funds'),
          icon: <WalletOutlined />,
          path: urls.funds,
          featureKey: FEATURE_KEYS.FUNDS,
        },
        {
          key: 'expenses',
          label: translate('Expenses'),
          icon: <CreditCardOutlined />,
          path: urls.expenses,
          featureKey: FEATURE_KEYS.EXPENSES,
        },
      ],
    });
  }

  items.push(
    {
      key: 'settings',
      label: translate('Settings'),
      icon: <SettingOutlined />,
      featureKey: FEATURE_KEYS.SETTINGS,
      children: [
        {
          key: 'general_settings',
          label: translate('General'),
          icon: <SettingOutlined />,
          path: urls.settings,
          featureKey: FEATURE_KEYS.SETTINGS,
        },
        {
          key: 'branches',
          label: translate('Branches'),
          icon: <BranchesOutlined />,
          path: urls.manageBranchs,
          featureKey: FEATURE_KEYS.BRANCHS,
        },
        {
          key: 'Audit Logs',
          label: translate('Audit Logs'),
          icon: <AuditOutlined />,
          path: urls.auditLogs,
          featureKey: FEATURE_KEYS.AUDIT_LOGS,
        },
      ],
    },
    {
      key: 'website-management',
      label: translate('Website'),
      icon: <GlobalOutlined />,
      children: [
        {
          key: 'pricing-plans',
          label: translate('Pricing Plans'),
          icon: <DollarOutlined />,
          path: urls.websitePricingPlans,
        },
        {
          key: 'website-visitors',
          label: translate('Visitors'),
          icon: <TeamOutlined />,
          path: urls.websiteVisitors,
        },
      ],
    }
  );

  return items;
};

// ============================================================================
// SUPER ADMIN SECTION
// ============================================================================
export const getSuperAdminMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: 'super_admin_dashboard',
    label: translate('Overview'),
    icon: <DashboardOutlined />,
    path: urls.superAdminDashboard,
  },
  {
    key: 'all_storefronts',
    label: translate('Online Storefronts'),
    icon: <ShoppingCartOutlined />,
    path: urls.superAdminStorefronts,
  },
  {
    key: 'platform_management',
    label: translate('Platform Control'),
    icon: <AppstoreOutlined />,
    children: [
      {
        key: 'all_shops',
        label: translate('Businesses'),
        icon: <ShopOutlined />,
        path: urls.superAdminShops,
      },
      {
        key: 'all_users',
        label: translate('Users'),
        icon: <TeamOutlined />,
        path: urls.superAdminUsers,
      },
      {
        key: 'all_payments',
        label: translate('Payments'),
        icon: <CreditCardOutlined />,
        path: urls.superAdminPayments,
      },
      {
        key: 'system_settings',
        label: translate('Configuration'),
        icon: <SettingOutlined />,
        path: urls.superAdminSettings,
      },
      {
        key: 'activity_logs',
        label: translate('Audit Logs'),
        icon: <HistoryOutlined />,
        path: urls.superAdminActivity,
      },
    ],
  },
  {
    key: 'website_management',
    label: translate('Website Hub'),
    icon: <GlobalOutlined />,
    children: [
      {
        key: 'pricing_plans',
        label: translate('Pricing Plans'),
        icon: <DollarOutlined />,
        path: urls.websitePricingPlans,
      },
      {
        key: 'website_visitors',
        label: translate('Visitors'),
        icon: <TeamOutlined />,
        path: urls.websiteVisitors,
      },
    ],
  },
];

// ============================================================================
// HOSPITAL SECTION
// ============================================================================
export const getHospitalMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: 'hospital_dashboard',
    label: translate('HIS Dashboard'),
    icon: <DashboardOutlined />,
    path: urls.hospitalDashboard,
    featureKey: FEATURE_KEYS.HOSPITAL_DASHBOARD,
    permissions: ['ADMIN', 'DOCTOR', 'NURSE'],
  },
  {
    key: 'hospital_reports',
    label: translate('Hospital Reports'),
    icon: <BarChartOutlined />,
    path: urls.hospitalReports,
    featureKey: FEATURE_KEYS.HOSPITAL_DASHBOARD,
    permissions: ['ADMIN', 'DOCTOR'],
  },
  {
    key: 'hospital_appointments',
    label: translate('Appointments'),
    icon: <CalendarOutlined />,
    path: urls.hospitalAppointments,
    featureKey: FEATURE_KEYS.CLINICAL_QUEUE,
    permissions: ['ADMIN', 'DOCTOR', 'NURSE'],
  },
  {
    key: 'patient_queue',
    label: translate('Patient Queue'),
    icon: <OrderedListOutlined />,
    path: urls.hospitalQueue,
    featureKey: FEATURE_KEYS.CLINICAL_QUEUE,
    permissions: ['NURSE', 'DOCTOR', 'LAB_TECH'],
  },
  {
    key: 'hospital_triage',
    label: translate('Triage'),
    icon: <HeartOutlined />,
    path: urls.hospitalTriage.replace('/:encounterId', ''),
    featureKey: FEATURE_KEYS.TRIAGE,
    permissions: ['NURSE', 'DOCTOR', 'ADMIN', 'OWNER', 'MANAGER', 'RECEPTIONIST'],
  },
  {
    key: 'hospital_ambulance',
    label: translate("Ambulance & Services d'Urgence"),
    icon: <CarOutlined />,
    path: urls.hospitalAmbulance,
    featureKey: FEATURE_KEYS.CLINICAL_QUEUE,
  },
  {
    key: 'hospital_consultation',
    label: translate('Consultation'),
    icon: <ExperimentOutlined />,
    path: urls.hospitalConsultation.replace('/:encounterId', ''),
    featureKey: FEATURE_KEYS.CONSULTATION,
    permissions: ['DOCTOR'],
  },
  {
    key: 'hospital_patients',
    label: translate('Patient Records'),
    icon: <SolutionOutlined />,
    path: urls.patients,
    featureKey: FEATURE_KEYS.PATIENTS,
    permissions: ['DOCTOR', 'NURSE'],
  },
  {
    key: 'hospital_labs',
    label: translate('Laboratory'),
    icon: <ExperimentOutlined />,
    path: urls.hospitalLabs,
    featureKey: FEATURE_KEYS.HOSPITAL_LABS,
    permissions: ['LAB_TECH', 'DOCTOR'],
  },
  {
    key: 'hospital_wards',
    label: translate('Inpatient Wards'),
    icon: <DashboardOutlined />,
    path: urls.hospitalWards,
    featureKey: FEATURE_KEYS.HOSPITAL_WARDS,
    permissions: ['NURSE', 'DOCTOR'],
  },
  {
    key: 'hospital_finance',
    label: translate('Billing & Escrow'),
    icon: <WalletOutlined />,
    path: urls.hospitalFinance,
    featureKey: FEATURE_KEYS.HOSPITAL_MANAGEMENT,
    permissions: ['ADMIN', 'NURSE', 'DOCTOR'],
  },
  {
    key: 'hospital_pacs',
    label: translate('PACS Imaging'),
    icon: <PictureOutlined />,
    path: urls.hospitalPACS,
    featureKey: FEATURE_KEYS.HOSPITAL_DASHBOARD,
    permissions: ['ADMIN', 'DOCTOR', 'NURSE'],
  },
  {
    key: 'hospital_settings',
    label: translate('Hospital Settings'),
    icon: <SettingOutlined />,
    path: urls.hospitalSettings,
    featureKey: FEATURE_KEYS.SETTINGS,
    permissions: ['ADMIN', 'OWNER'],
  },
];

// ============================================================================
// HRM & PAYROLL SECTION
// ============================================================================
export const getHRMMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: 'hrm',
    label: translate('Staff Management'),
    icon: <TeamOutlined />,
    featureKey: FEATURE_KEYS.HRM,
    children: [
      {
        key: 'employees',
        label: translate('Employees'),
        icon: <UserOutlined />,
        path: urls.hrmEmployees,
        featureKey: FEATURE_KEYS.HRM,
      },
      {
        key: 'payroll',
        label: translate('Payroll'),
        icon: <CreditCardOutlined />,
        path: urls.hrmPayroll,
        featureKey: FEATURE_KEYS.PAYROLL,
      },
    ],
  },
];

// ============================================================================
// SHOE STORE MANAGEMENT — Sidebar items
// ============================================================================
export const getShoeMenuItems = (translate: (key: string) => string): MenuItem[] => [
  // ── SALES & POS ───────────────────────────────────────────────────────────
  {
    key: 'shoe_pos',
    label: translate('Point of Sale'),
    icon: <ShoppingCartOutlined />,
    path: urls.shoePOS,
    featureKey: FEATURE_KEYS.SHOE_POS,
  },
  {
    key: 'shoe_sales_history',
    label: translate('Sales History'),
    icon: <HistoryOutlined />,
    path: urls.shoeSalesHistory,
    featureKey: FEATURE_KEYS.SALES_HISTORY,
  },
  {
    key: 'shoe_prepacks',
    label: translate('Carton Pre-Packs'),
    icon: <InboxOutlined />,
    path: urls.shoePrepacks,
    featureKey: FEATURE_KEYS.SHOE_PREPACKS,
  },
  // ── INVENTORY & CATALOG ──────────────────────────────────────────────────
  {
    key: 'shoe_catalog',
    label: translate('Footwear Catalog'),
    icon: <AppstoreOutlined />,
    path: urls.shoeCatalog,
    featureKey: FEATURE_KEYS.SHOE_CATALOG,
  },
  {
    key: 'shoe_suppliers',
    label: translate('Brand Suppliers'),
    icon: <ShopOutlined />,
    path: urls.suppliers,
    featureKey: FEATURE_KEYS.SUPPLIERS,
  },
  // ── FLOOR & DISPLAY ──────────────────────────────────────────────────────
  {
    key: 'shoe_audits',
    label: translate('Display Audits'),
    icon: <AuditOutlined />,
    path: urls.shoeAudits,
    featureKey: FEATURE_KEYS.SHOE_AUDITS,
  },
  {
    key: 'shoe_mismatched',
    label: translate('Mismatched Clearance'),
    icon: <SwapOutlined />,
    path: urls.shoeMismatched,
    featureKey: FEATURE_KEYS.SHOE_MISMATCHED,
  },
  // ── SERVICES & TOOLS ─────────────────────────────────────────────────────
  {
    key: 'shoe_care',
    label: translate('Care & Repair Orders'),
    icon: <ToolOutlined />,
    path: urls.shoeCare,
    featureKey: FEATURE_KEYS.SHOE_CARE,
  },
  {
    key: 'shoe_labels',
    label: translate('Box Barcode Labels'),
    icon: <TagsOutlined />,
    path: urls.shoeLabels,
    featureKey: FEATURE_KEYS.SHOE_LABELS,
  },
  {
    key: 'shoe_size_converter',
    label: translate('Size Converter'),
    icon: <GlobalOutlined />,
    path: urls.shoeSizeConverter,
    featureKey: FEATURE_KEYS.SHOE_SIZE_CONVERTER,
  },
  // ── CUSTOMERS & CREDIT ───────────────────────────────────────────────────
  {
    key: 'shoe_customers',
    label: translate('Customers'),
    icon: <UserSwitchOutlined />,
    path: urls.customers,
    featureKey: FEATURE_KEYS.CUSTOMERS,
  },
  {
    key: 'shoe_debts',
    label: translate('Debts & Credit'),
    icon: <CreditCardOutlined />,
    path: urls.debts,
    featureKey: FEATURE_KEYS.DEBTS,
  },
  // ── ANALYTICS & REPORTS ──────────────────────────────────────────────────
  {
    key: 'shoe_sales_analytics',
    label: translate('Sales Analytics'),
    icon: <LineChartOutlined />,
    path: urls.salesAnalytics,
    featureKey: FEATURE_KEYS.SALES_ANALYTICS,
  },
  {
    key: 'shoe_reports',
    label: translate('Reports'),
    icon: <SnippetsOutlined />,
    path: urls.reports,
    featureKey: FEATURE_KEYS.REPORTS,
  },
  {
    key: 'shoe_audit_logs',
    label: translate('Audit Logs'),
    icon: <FileProtectOutlined />,
    path: urls.auditLogs,
    featureKey: FEATURE_KEYS.AUDIT_LOGS,
  },
];

// ============================================================================
// HARDWARE STORE MANAGEMENT — Sidebar items
// ============================================================================
export const getHardwareMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: 'hardware_dashboard',
    label: translate('Dashboard'),
    icon: <DashboardOutlined />,
    path: urls.hardwareDashboard,
    featureKey: FEATURE_KEYS.HARDWARE_DASHBOARD,
  },
  {
    key: 'hardware_pos',
    label: translate('Point of Sale'),
    icon: <ShoppingCartOutlined />,
    path: urls.hardwarePOS,
    featureKey: FEATURE_KEYS.POS_V2,
  },
  {
    key: 'hardware_inventory',
    label: translate('Stock Catalog'),
    icon: <AppstoreOutlined />,
    path: urls.hardwareCatalog,
    featureKey: FEATURE_KEYS.PRODUCTS,
  },
  {
    key: 'hardware_rentals',
    label: translate('Tool Rentals'),
    icon: <ToolOutlined />,
    path: urls.hardwareRentals,
    featureKey: FEATURE_KEYS.HARDWARE_DASHBOARD,
  },
  {
    key: 'hardware_customers',
    label: translate('Contractors & Clients'),
    icon: <UserSwitchOutlined />,
    path: urls.hardwareCustomers,
    featureKey: FEATURE_KEYS.CUSTOMERS,
  },
  {
    key: 'hardware_debts',
    label: translate('Credit & Debts'),
    icon: <CreditCardOutlined />,
    path: urls.hardwareDebts,
    featureKey: FEATURE_KEYS.DEBTS,
  },
  {
    key: 'hardware_funds',
    label: translate('Funds & Ledger'),
    icon: <WalletOutlined />,
    path: urls.funds,
    featureKey: FEATURE_KEYS.FUNDS,
  },
  {
    key: 'hardware_expenses',
    label: translate('Expenses'),
    icon: <DollarOutlined />,
    path: urls.expenses,
    featureKey: FEATURE_KEYS.EXPENSES,
  },
  {
    key: 'hardware_analytics',
    label: translate('Sales Analytics'),
    icon: <LineChartOutlined />,
    path: urls.hardwareAnalytics,
    featureKey: FEATURE_KEYS.SALES_ANALYTICS,
  },
  {
    key: 'hardware_reports',
    label: translate('Inventory Reports'),
    icon: <SnippetsOutlined />,
    path: urls.hardwareReports,
    featureKey: FEATURE_KEYS.REPORTS,
  },
];

export const getMobileMoneyMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: 'momo_dashboard',
    label: translate('Executive Dashboard'),
    icon: <DashboardOutlined />,
    path: urls.mobileMoneyDashboard,
    featureKey: FEATURE_KEYS.MOBILE_MONEY_DASHBOARD,
  },
  {
    key: 'momo_pos',
    label: translate('Agent Terminal POS'),
    icon: <MobileOutlined />,
    path: urls.mobileMoneyPOS,
    featureKey: FEATURE_KEYS.MOBILE_MONEY_POS,
  },
  {
    key: 'momo_floats',
    label: translate('E-Floats & Cash Till'),
    icon: <WalletOutlined />,
    path: urls.mobileMoneyFloats,
    featureKey: FEATURE_KEYS.MOBILE_MONEY_FLOATS,
  },
  {
    key: 'momo_ledger',
    label: translate('Digital Ledger'),
    icon: <BookOutlined />,
    path: urls.mobileMoneyLedger,
    featureKey: FEATURE_KEYS.MOBILE_MONEY_LEDGER,
  },
  {
    key: 'momo_reconciliation',
    label: translate('Shift Audit & Close'),
    icon: <AuditOutlined />,
    path: urls.mobileMoneyReconciliation,
    featureKey: FEATURE_KEYS.MOBILE_MONEY_RECONCILIATION,
  },
  {
    key: 'momo_debts',
    label: translate('Dettes & Crédits Client'),
    icon: <WarningOutlined />,
    path: urls.mobileMoneyDebts,
  },
  {
    key: 'momo_customer_history',
    label: translate('Historique Clients'),
    icon: <HistoryOutlined />,
    path: urls.mobileMoneyCustomerHistory,
  },
];

export const getHotelMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: 'hotel_dashboard',
    label: translate('Hotel Overview & Analytics'),
    icon: <DashboardOutlined />,
    path: urls.hotelDashboard,
    featureKey: FEATURE_KEYS.HOTEL_DASHBOARD,
  },
  {
    key: 'hotel_rooms',
    label: translate('Live Room Rack & Floors'),
    icon: <AppstoreOutlined />,
    path: urls.hotelRooms,
    featureKey: FEATURE_KEYS.HOTEL_ROOMS,
  },
  {
    key: 'hotel_reservations',
    label: translate('Front Desk & Bookings'),
    icon: <CalendarOutlined />,
    path: urls.hotelReservations,
    featureKey: FEATURE_KEYS.HOTEL_RESERVATIONS,
  },
  {
    key: 'hotel_housekeeping',
    label: translate('Housekeeping & Turnover'),
    icon: <AuditOutlined />,
    path: urls.hotelHousekeeping,
    featureKey: FEATURE_KEYS.HOTEL_HOUSEKEEPING,
  },
  {
    key: 'hotel_folios',
    label: translate('Guest Folios & Billing'),
    icon: <CreditCardOutlined />,
    path: urls.hotelFolios,
    featureKey: FEATURE_KEYS.HOTEL_FOLIOS,
  },
  {
    key: 'hotel_reports',
    label: translate('Audit & Financial Reports'),
    icon: <SnippetsOutlined />,
    path: urls.hotelReports,
    featureKey: FEATURE_KEYS.HOTEL_REPORTS,
  },
  {
    key: 'hotel_guests',
    label: translate('Guest Directory & CRM'),
    icon: <TeamOutlined />,
    path: urls.hotelGuests,
    featureKey: FEATURE_KEYS.HOTEL_GUESTS,
  },
];

// ============================================================================
// CLOTHING & FASHION BOUTIQUE MANAGEMENT — Sidebar items
// ============================================================================
export const getClothingMenuItems = (translate: (key: string) => string): MenuItem[] => [
  // ── BOUTIQUE & POS ─────────────────────────────────────────────────────────
  {
    key: 'clothing_pos',
    label: translate('Boutique POS Cashier'),
    icon: <ShoppingCartOutlined />,
    path: urls.pos,
    featureKey: FEATURE_KEYS.POS_V2,
  },
  {
    key: 'clothing_sales_history',
    label: translate('Sales & Fitting Receipts'),
    icon: <HistoryOutlined />,
    path: urls.salesHistory,
    featureKey: FEATURE_KEYS.SALES_HISTORY,
  },
  // ── APPAREL & STYLES ───────────────────────────────────────────────────────
  {
    key: 'clothing_matrix',
    label: translate('Apparel Catalog & Matrix'),
    icon: <SkinOutlined />,
    path: urls.clothingDashboard,
    featureKey: FEATURE_KEYS.CLOTHING_DASHBOARD,
  },
  {
    key: 'clothing_stock',
    label: translate('Garment Inventory & Stock'),
    icon: <InboxOutlined />,
    path: urls.stock,
    featureKey: FEATURE_KEYS.INVENTORY,
  },
  // ── FITTING & ALTERATIONS ─────────────────────────────────────────────────
  {
    key: 'clothing_fitting',
    label: translate('Fitting Room Trials'),
    icon: <LockOutlined />,
    path: urls.clothingDashboard,
    featureKey: FEATURE_KEYS.CLOTHING_DASHBOARD,
  },
  {
    key: 'clothing_alterations',
    label: translate('Tailor Alteration Tickets'),
    icon: <ScissorOutlined />,
    path: urls.clothingDashboard,
    featureKey: FEATURE_KEYS.CLOTHING_DASHBOARD,
  },
  // ── SUPPLIERS & BRANDS ─────────────────────────────────────────────────────
  {
    key: 'clothing_suppliers',
    label: translate('Fashion Brands & Suppliers'),
    icon: <ShopOutlined />,
    path: urls.suppliers,
    featureKey: FEATURE_KEYS.SUPPLIERS,
  },
  // ── CLIENTS & STORE CREDIT ─────────────────────────────────────────────────
  {
    key: 'clothing_customers',
    label: translate('VIP Clients & Stylist Bookings'),
    icon: <TeamOutlined />,
    path: urls.customers,
    featureKey: FEATURE_KEYS.CUSTOMERS,
  },
  {
    key: 'clothing_debts',
    label: translate('Customer Store Credit'),
    icon: <WarningOutlined />,
    path: urls.debts,
    featureKey: FEATURE_KEYS.CUSTOMERS,
  },
  // ── ANALYTICS & REPORTS ───────────────────────────────────────────────────
  {
    key: 'clothing_analytics',
    label: translate('Fashion Sales Analytics'),
    icon: <LineChartOutlined />,
    path: urls.salesAnalytics,
    featureKey: FEATURE_KEYS.SALES_ANALYTICS,
  },
  {
    key: 'clothing_reports',
    label: translate('Apparel Reports'),
    icon: <AuditOutlined />,
    path: urls.reports,
    featureKey: FEATURE_KEYS.REPORTS,
  },
];





// ============================================================================
// GROCERY & SUPERMARKET MANAGEMENT — Sidebar items
// ============================================================================
export const getGroceryMenuItems = (translate: (key: string) => string): MenuItem[] => [
  // ── SALES & SCALE POS ──
  {
    key: 'grocery_pos',
    label: translate('Scale POS & Checkout'),
    icon: <ShoppingCartOutlined />,
    path: urls.groceryPOS,
    featureKey: FEATURE_KEYS.GROCERY_POS,
  },
  {
    key: 'grocery_sales_history',
    label: translate('Sales History'),
    icon: <HistoryOutlined />,
    path: urls.grocerySalesHistory,
    featureKey: FEATURE_KEYS.SALES_HISTORY,
  },
  // ── FRESH PRODUCE & INVENTORY ──
  {
    key: 'grocery_inventory',
    label: translate('Produce & Catalog'),
    icon: <AppstoreOutlined />,
    path: urls.groceryInventory,
    featureKey: FEATURE_KEYS.GROCERY_INVENTORY,
  },
  {
    key: 'grocery_expiries',
    label: translate('Expiries & Markdowns'),
    icon: <WarningOutlined />,
    path: urls.groceryExpiries,
    featureKey: FEATURE_KEYS.GROCERY_EXPIRIES,
  },
  {
    key: 'grocery_spoilage',
    label: translate('Spoilage & Food Waste'),
    icon: <DeleteOutlined />,
    path: urls.grocerySpoilage,
    featureKey: FEATURE_KEYS.GROCERY_SPOILAGE,
  },
  {
    key: 'grocery_suppliers',
    label: translate('Suppliers'),
    icon: <ShopOutlined />,
    path: urls.suppliers,
    featureKey: FEATURE_KEYS.SUPPLIERS,
  },
  // ── CUSTOMERS ──
  {
    key: 'grocery_customers',
    label: translate('Customers'),
    icon: <UserSwitchOutlined />,
    path: urls.customers,
    featureKey: FEATURE_KEYS.CUSTOMERS,
  },
  // ── REPORTS & AUDIT ──
  {
    key: 'grocery_reports',
    label: translate('Reports'),
    icon: <SnippetsOutlined />,
    path: urls.groceryReports,
    featureKey: FEATURE_KEYS.GROCERY_REPORTS,
  },
  {
    key: 'grocery_audit_logs',
    label: translate('Audit Logs'),
    icon: <FileProtectOutlined />,
    path: urls.auditLogs,
    featureKey: FEATURE_KEYS.AUDIT_LOGS,
  },
];

// ============================================================================
// JEWELRY & VAULT MANAGEMENT — Sidebar items
// ============================================================================
export const getJewelryMenuItems = (translate: (key: string) => string): MenuItem[] => [
  // ── SALES & POS ──
  {
    key: 'jewelry_pos',
    label: translate('Point of Sale'),
    icon: <ShoppingCartOutlined />,
    path: urls.jewelryPOS,
    featureKey: FEATURE_KEYS.JEWELRY_POS,
  },
  {
    key: 'jewelry_sales_history',
    label: translate('Sales History'),
    icon: <HistoryOutlined />,
    path: urls.jewelrySalesHistory,
    featureKey: FEATURE_KEYS.SALES_HISTORY,
  },
  // ── VAULT & INVENTORY ──
  {
    key: 'jewelry_vault',
    label: translate('Vault Inventory'),
    icon: <LockOutlined />,
    path: urls.jewelryVault,
    featureKey: FEATURE_KEYS.JEWELRY_VAULT,
  },
  {
    key: 'jewelry_certificates',
    label: translate('Gemstone Certificates'),
    icon: <SafetyCertificateOutlined />,
    path: urls.jewelryCertificates,
    featureKey: FEATURE_KEYS.JEWELRY_CERTIFICATES,
  },
  {
    key: 'jewelry_suppliers',
    label: translate('Suppliers'),
    icon: <ShopOutlined />,
    path: urls.suppliers,
    featureKey: FEATURE_KEYS.SUPPLIERS,
  },
  // ── PRECIOUS METALS ──
  {
    key: 'jewelry_spot_rates',
    label: translate('Metal Spot Rates'),
    icon: <GoldOutlined />,
    path: urls.jewelrySpotRates,
    featureKey: FEATURE_KEYS.JEWELRY_SPOT_RATES,
  },
  {
    key: 'jewelry_trade_ins',
    label: translate('Trade-In & Scrap Buyback'),
    icon: <SwapOutlined />,
    path: urls.jewelryTradeIns,
    featureKey: FEATURE_KEYS.JEWELRY_TRADE_INS,
  },
  // ── CUSTOMERS ──
  {
    key: 'jewelry_customers',
    label: translate('Customers'),
    icon: <UserSwitchOutlined />,
    path: urls.customers,
    featureKey: FEATURE_KEYS.CUSTOMERS,
  },
  // ── ANALYTICS & REPORTS ──
  {
    key: 'jewelry_reports',
    label: translate('Reports'),
    icon: <SnippetsOutlined />,
    path: urls.jewelryReports,
    featureKey: FEATURE_KEYS.JEWELRY_REPORTS,
  },
  {
    key: 'jewelry_audit_logs',
    label: translate('Audit Logs'),
    icon: <FileProtectOutlined />,
    path: urls.auditLogs,
    featureKey: FEATURE_KEYS.AUDIT_LOGS,
  },
];

// ============================================================================
// COFFEE SHOP & ARTISAN ROASTER — Sidebar items
// ============================================================================
export const getCoffeeShopMenuItems = (translate: (key: string) => string): MenuItem[] => [
  // ── CASHIER & POS ──
  {
    key: 'coffee_pos',
    label: translate('Barista Cashier POS'),
    icon: <ThunderboltOutlined />,
    path: urls.coffeePOS,
    featureKey: FEATURE_KEYS.COFFEE_SHOP_POS,
  },
  {
    key: 'coffee_orders',
    label: translate('Orders & Brewing Queue'),
    icon: <OrderedListOutlined />,
    path: urls.coffeeOrders,
    featureKey: FEATURE_KEYS.COFFEE_SHOP_ORDERS,
  },
  {
    key: 'coffee_loyalty',
    label: translate('Stamp Cards & Loyalty'),
    icon: <StarOutlined />,
    path: urls.coffeeLoyalty,
    featureKey: FEATURE_KEYS.COFFEE_SHOP_LOYALTY,
  },
  // ── BEVERAGES & RECIPES ──
  {
    key: 'coffee_recipes',
    label: translate('Drink Recipes & Depletion'),
    icon: <ExperimentOutlined />,
    path: urls.coffeeRecipes,
    featureKey: FEATURE_KEYS.COFFEE_SHOP_RECIPES,
  },
  {
    key: 'coffee_modifiers',
    label: translate('Milk & Syrup Modifiers'),
    icon: <AppstoreOutlined />,
    path: urls.coffeeModifiers,
    featureKey: FEATURE_KEYS.COFFEE_SHOP_MODIFIERS,
  },
  {
    key: 'coffee_spillage',
    label: translate('Spillage & Waste Audits'),
    icon: <DeleteOutlined />,
    path: urls.coffeeSpillage,
    featureKey: FEATURE_KEYS.COFFEE_SHOP_SPILLAGE,
  },
  // ── OPERATIONS & SHIFTS ──
  {
    key: 'coffee_shifts',
    label: translate('Barista Shifts & Till Float'),
    icon: <ClockCircleOutlined />,
    path: urls.coffeeShifts,
    featureKey: FEATURE_KEYS.COFFEE_SHOP_SHIFTS,
  },
  {
    key: 'coffee_stock',
    label: translate('Beans & Packaging Stock'),
    icon: <DatabaseOutlined />,
    path: urls.stock,
    featureKey: FEATURE_KEYS.STOCK,
  },
  {
    key: 'coffee_movements',
    label: translate('Stock Movements'),
    icon: <SwapOutlined />,
    path: urls.stockMovements,
    featureKey: FEATURE_KEYS.STOCK_MOVEMENTS,
  },
  {
    key: 'coffee_suppliers',
    label: translate('Suppliers & Roasters'),
    icon: <ShopOutlined />,
    path: urls.suppliers,
    featureKey: FEATURE_KEYS.SUPPLIERS,
  },
  // ── CUSTOMERS & AUDIT ──
  {
    key: 'coffee_customers',
    label: translate('Customers Directory'),
    icon: <UserSwitchOutlined />,
    path: urls.customers,
    featureKey: FEATURE_KEYS.CUSTOMERS,
  },
  {
    key: 'coffee_reports',
    label: translate('Sales & Shift Reports'),
    icon: <SnippetsOutlined />,
    path: urls.reports,
    featureKey: FEATURE_KEYS.REPORTS,
  },
  {
    key: 'coffee_audit_logs',
    label: translate('Audit Logs'),
    icon: <FileProtectOutlined />,
    path: urls.auditLogs,
    featureKey: FEATURE_KEYS.AUDIT_LOGS,
  },
];

// ============================================================================
// WHOLESALE FMCG & B2B TRADE — Sidebar items
// ============================================================================
export const getWholesaleMenuItems = (translate: (key: string) => string): MenuItem[] => [
  // ── B2B WORKSTATION ──
  {
    key: 'wholesale_pos',
    label: translate('Wholesale Workstation'),
    icon: <GoldOutlined />,
    path: urls.wholesale,
    featureKey: FEATURE_KEYS.WHOLESALE,
  },
  {
    key: 'wholesale_sales_history',
    label: translate('B2B Sales Invoices'),
    icon: <HistoryOutlined />,
    path: urls.salesHistory,
    featureKey: FEATURE_KEYS.SALES_HISTORY,
  },
  {
    key: 'wholesale_clients',
    label: translate('Corporate Accounts'),
    icon: <TeamOutlined />,
    path: urls.customers,
    featureKey: FEATURE_KEYS.CUSTOMERS,
  },
  {
    key: 'wholesale_debts',
    label: translate('Credit Limits & Terms'),
    icon: <CreditCardOutlined />,
    path: urls.debts,
    featureKey: FEATURE_KEYS.DEBTS,
  },
  // ── WAREHOUSE & INVENTORY ──
  {
    key: 'wholesale_catalog',
    label: translate('Products & Tiered Pricing'),
    icon: <AppstoreOutlined />,
    path: urls.products,
    featureKey: FEATURE_KEYS.PRODUCTS,
  },
  {
    key: 'wholesale_stock',
    label: translate('Warehouse Inventory'),
    icon: <DatabaseOutlined />,
    path: urls.stock,
    featureKey: FEATURE_KEYS.STOCK,
  },
  {
    key: 'wholesale_movements',
    label: translate('Stock Movements'),
    icon: <SwapOutlined />,
    path: urls.stockMovements,
    featureKey: FEATURE_KEYS.STOCK_MOVEMENTS,
  },
  // ── PROCUREMENT & LOGISTICS ──
  {
    key: 'wholesale_purchase_orders',
    label: translate('Purchase Orders'),
    icon: <FileAddOutlined />,
    path: urls.purchaseOrders,
    featureKey: FEATURE_KEYS.PURCHASE_ORDERS,
  },
  {
    key: 'wholesale_delivery_notes',
    label: translate('Delivery Notes'),
    icon: <TruckOutlined />,
    path: urls.deliveryNotes,
    featureKey: FEATURE_KEYS.DELIVERY_NOTES,
  },
  {
    key: 'wholesale_suppliers',
    label: translate('Suppliers'),
    icon: <ShopOutlined />,
    path: urls.suppliers,
    featureKey: FEATURE_KEYS.SUPPLIERS,
  },
  // ── REPORTS ──
  {
    key: 'wholesale_reports',
    label: translate('B2B Revenue Reports'),
    icon: <SnippetsOutlined />,
    path: urls.reports,
    featureKey: FEATURE_KEYS.REPORTS,
  },
  {
    key: 'wholesale_audit_logs',
    label: translate('Audit Logs'),
    icon: <FileProtectOutlined />,
    path: urls.auditLogs,
    featureKey: FEATURE_KEYS.AUDIT_LOGS,
  },
];

// ============================================================================
// REGIONAL DISTRIBUTOR & LOGISTICS — Sidebar items
// ============================================================================
export const getDistributorMenuItems = (translate: (key: string) => string): MenuItem[] => [
  // ── LOGISTICS & FLEET ──
  {
    key: 'distributor_delivery_notes',
    label: translate('Delivery Notes & Fleet'),
    icon: <TruckOutlined />,
    path: urls.deliveryNotes,
    featureKey: FEATURE_KEYS.DELIVERY_NOTES,
  },
  {
    key: 'distributor_purchase_orders',
    label: translate('Inbound Procurement (PO)'),
    icon: <FileAddOutlined />,
    path: urls.purchaseOrders,
    featureKey: FEATURE_KEYS.PURCHASE_ORDERS,
  },
  {
    key: 'distributor_suppliers',
    label: translate('Manufacturers & Carriers'),
    icon: <ShopOutlined />,
    path: urls.suppliers,
    featureKey: FEATURE_KEYS.SUPPLIERS,
  },
  // ── DISTRIBUTION SALES ──
  {
    key: 'distributor_workstation',
    label: translate('Depot Wholesale POS'),
    icon: <GoldOutlined />,
    path: urls.wholesale,
    featureKey: FEATURE_KEYS.WHOLESALE,
  },
  {
    key: 'distributor_invoices',
    label: translate('Dispatched Invoices'),
    icon: <HistoryOutlined />,
    path: urls.salesHistory,
    featureKey: FEATURE_KEYS.SALES_HISTORY,
  },
  {
    key: 'distributor_clients',
    label: translate('Distribution Outlets'),
    icon: <TeamOutlined />,
    path: urls.customers,
    featureKey: FEATURE_KEYS.CUSTOMERS,
  },
  {
    key: 'distributor_credit',
    label: translate('Accounts Receivable'),
    icon: <CreditCardOutlined />,
    path: urls.debts,
    featureKey: FEATURE_KEYS.DEBTS,
  },
  // ── DEPOT STORAGE ──
  {
    key: 'distributor_stock',
    label: translate('Depot Storage Stock'),
    icon: <DatabaseOutlined />,
    path: urls.stock,
    featureKey: FEATURE_KEYS.STOCK,
  },
  {
    key: 'distributor_catalog',
    label: translate('Product Lines'),
    icon: <AppstoreOutlined />,
    path: urls.products,
    featureKey: FEATURE_KEYS.PRODUCTS,
  },
  {
    key: 'distributor_movements',
    label: translate('Hub Transfers & Movements'),
    icon: <SwapOutlined />,
    path: urls.stockMovements,
    featureKey: FEATURE_KEYS.STOCK_MOVEMENTS,
  },
  // ── REPORTS ──
  {
    key: 'distributor_reports',
    label: translate('Supply Chain Reports'),
    icon: <SnippetsOutlined />,
    path: urls.reports,
    featureKey: FEATURE_KEYS.REPORTS,
  },
  {
    key: 'distributor_audit_logs',
    label: translate('Audit Logs'),
    icon: <FileProtectOutlined />,
    path: urls.auditLogs,
    featureKey: FEATURE_KEYS.AUDIT_LOGS,
  },
];


// ============================================================================
// 1. REAL ESTATE & PROPERTY MANAGEMENT — Sidebar items
// ============================================================================
export const getRealEstateMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: "re_properties",
    label: translate("Properties & Estates"),
    icon: <AppstoreOutlined />,
    path: urls.realEstateProperties,
  },
  {
    key: "re_units",
    label: translate("Units & Lease Matrix"),
    icon: <TableOutlined />,
    path: urls.realEstateUnits,
  },
  {
    key: "re_rent_payments",
    label: translate("Rent Roll & Collections"),
    icon: <DollarOutlined />,
    path: urls.realEstateRentPayments,
  },
  {
    key: "re_maintenance",
    label: translate("Facilities & Maintenance"),
    icon: <ToolOutlined />,
    path: urls.realEstateMaintenance,
  },
  {
    key: "re_tenants",
    label: translate("Tenants Directory"),
    icon: <TeamOutlined />,
    path: urls.customers,
  },
  {
    key: "re_arrears",
    label: translate("Rent Arrears & Debts"),
    icon: <CreditCardOutlined />,
    path: urls.debts,
  },
  {
    key: "re_reports",
    label: translate("Portfolio Analytics"),
    icon: <LineChartOutlined />,
    path: urls.reports,
  },
];

// ============================================================================
// 2. BARBERSHOP & MEN'S GROOMING — Sidebar items
// ============================================================================
export const getBarbershopMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: "barber_pos",
    label: translate("Walk-In Queue & POS"),
    icon: <ThunderboltOutlined />,
    path: urls.barbershopPOS,
  },
  {
    key: "barber_chairs",
    label: translate("Barber Chairs & Commissions"),
    icon: <UserSwitchOutlined />,
    path: urls.barbershopChairs,
  },
  {
    key: "barber_services",
    label: translate("Grooming & Cuts Menu"),
    icon: <ScissorOutlined />,
    path: urls.barbershopServices,
  },
  {
    key: "barber_products",
    label: translate("Pomades & Grooming Stock"),
    icon: <InboxOutlined />,
    path: urls.products,
  },
  {
    key: "barber_clients",
    label: translate("VIP Grooming Clients"),
    icon: <TeamOutlined />,
    path: urls.customers,
  },
  {
    key: "barber_reports",
    label: translate("Shift & Revenue Reports"),
    icon: <SnippetsOutlined />,
    path: urls.reports,
  },
];

// ============================================================================
// 3. SPA & WELLNESS SANCTUARY — Sidebar items
// ============================================================================
export const getSpaMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: "spa_bookings",
    label: translate("Session Desk & Bookings"),
    icon: <CalendarOutlined />,
    path: urls.spaBookings,
  },
  {
    key: "spa_rooms",
    label: translate("Sanctuary Suites & Rooms"),
    icon: <EnvironmentOutlined />,
    path: urls.spaRooms,
  },
  {
    key: "spa_treatments",
    label: translate("Rituals & Treatment Menu"),
    icon: <HeartOutlined />,
    path: urls.spaTreatments,
  },
  {
    key: "spa_memberships",
    label: translate("VIP Wellness Passes"),
    icon: <CrownOutlined />,
    path: urls.spaMemberships,
  },
  {
    key: "spa_oils_stock",
    label: translate("Essential Oils & Retail Stock"),
    icon: <ExperimentOutlined />,
    path: urls.products,
  },
  {
    key: "spa_guests",
    label: translate("Guest Wellness Profiles"),
    icon: <TeamOutlined />,
    path: urls.customers,
  },
  {
    key: "spa_reports",
    label: translate("Spa Revenue Reports"),
    icon: <SnippetsOutlined />,
    path: urls.reports,
  },
];

// ============================================================================
// 4. TAILORING & CUSTOM COUTURE ATELIER — Sidebar items
// ============================================================================
export const getTailoringMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: "tailor_orders",
    label: translate("Bespoke Orders & Production"),
    icon: <ScissorOutlined />,
    path: urls.tailoringOrders,
  },
  {
    key: "tailor_measurements",
    label: translate("Client Body Measurements"),
    icon: <SolutionOutlined />,
    path: urls.tailoringMeasurements,
  },
  {
    key: "tailor_fabrics",
    label: translate("Fabric Rolls & Textiles"),
    icon: <SkinOutlined />,
    path: urls.tailoringFabrics,
  },
  {
    key: "tailor_clients",
    label: translate("Atelier Clientele"),
    icon: <TeamOutlined />,
    path: urls.customers,
  },
  {
    key: "tailor_balances",
    label: translate("Deposits & Balances Due"),
    icon: <CreditCardOutlined />,
    path: urls.debts,
  },
  {
    key: "tailor_reports",
    label: translate("Atelier Financial Reports"),
    icon: <SnippetsOutlined />,
    path: urls.reports,
  },
];

// ============================================================================
// 5. PROFESSIONAL SERVICES & CONSULTING — Sidebar items
// ============================================================================
export const getProfessionalServicesMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: "ps_projects",
    label: translate("Client Engagements & Matters"),
    icon: <ReconciliationOutlined />,
    path: urls.professionalServicesProjects,
  },
  {
    key: "ps_timesheets",
    label: translate("Billable Hours & Timesheets"),
    icon: <ClockCircleOutlined />,
    path: urls.professionalServicesTimesheets,
  },
  {
    key: "ps_retainers",
    label: translate("Corporate Retainers (MRR)"),
    icon: <SafetyCertificateOutlined />,
    path: urls.professionalServicesRetainers,
  },
  {
    key: "ps_clients",
    label: translate("Corporate Clients CRM"),
    icon: <TeamOutlined />,
    path: urls.customers,
  },
  {
    key: "ps_receivables",
    label: translate("Unpaid Invoices & WIP"),
    icon: <CreditCardOutlined />,
    path: urls.debts,
  },
  {
    key: "ps_reports",
    label: translate("Practice Utilization Reports"),
    icon: <LineChartOutlined />,
    path: urls.reports,
  },
];

// ============================================================================
// 6. HAIR SALON & BEAUTY STUDIO — Sidebar items
// ============================================================================
export const getSalonMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: "salon_appointments",
    label: translate("Chair Bookings & Checkout"),
    icon: <CalendarOutlined />,
    path: urls.salonAppointments,
  },
  {
    key: "salon_stations",
    label: translate("Stylist Stations & Chairs"),
    icon: <UserSwitchOutlined />,
    path: urls.salonStations,
  },
  {
    key: "salon_services",
    label: translate("Coiffure, Nails & Glam Menu"),
    icon: <ScissorOutlined />,
    path: urls.salonServices,
  },
  {
    key: "salon_formulas",
    label: translate("Color & Chemical Formulas"),
    icon: <ExperimentOutlined />,
    path: urls.salonFormulas,
  },
  {
    key: "salon_retail",
    label: translate("Beauty & Hair Care Retail"),
    icon: <ShoppingCartOutlined />,
    path: urls.products,
  },
  {
    key: "salon_clients",
    label: translate("Salon Clients Directory"),
    icon: <TeamOutlined />,
    path: urls.customers,
  },
  {
    key: "salon_reports",
    label: translate("Commission & Sales Reports"),
    icon: <SnippetsOutlined />,
    path: urls.reports,
  },
];

// ============================================================================
// 7. CAR RENTAL & FLEET OPERATIONS — Sidebar items
// ============================================================================
export const getCarRentalMenuItems = (translate: (key: string) => string): MenuItem[] => [
  {
    key: "cr_vehicles",
    label: translate("Vehicle Fleet & Availability"),
    icon: <CarOutlined />,
    path: urls.carRentalVehicles,
  },
  {
    key: "cr_agreements",
    label: translate("Rental Dispatch & Agreements"),
    icon: <FileProtectOutlined />,
    path: urls.carRentalAgreements,
  },
  {
    key: "cr_inspections",
    label: translate("Check-Out / Return Inspections"),
    icon: <AuditOutlined />,
    path: urls.carRentalInspections,
  },
  {
    key: "cr_clients",
    label: translate("Hirers & Corporate Accounts"),
    icon: <TeamOutlined />,
    path: urls.customers,
  },
  {
    key: "cr_deposits",
    label: translate("Security Deposits & Claims"),
    icon: <CreditCardOutlined />,
    path: urls.debts,
  },
  {
    key: "cr_reports",
    label: translate("Fleet Revenue Analytics"),
    icon: <LineChartOutlined />,
    path: urls.reports,
  },
];
